<script lang="ts">
	import { afterUpdate, createEventDispatcher } from 'svelte';
	import { format } from 'svelte-i18n';

	import { devices } from 'src/stores/lists/devices';
	import { groups } from 'src/stores/lists/groups';
	import { rooms } from 'src/stores/lists/rooms';
	import { routeParams } from 'src/stores/ui/viewport';

	import {
		resetWizardItems,
		setWizardSteps,
		updateWizardStepOnPrevNext,
		updateWizardStepOnWizardItem,
		requestSaveGroup,
		scrollToElement,
		requestReorderDevice,
		sortByPosition,
	} from '../../Lists/list-helpers';
	import { capFirstChar, deepCopy } from '../../../helpers/utilities';

	//Design system components
	import {
		Modal,
		Button,
		Wizard,
		WizardItem,
		WizardContainer,
		WizardContent,
	} from '@Shelly/shelly-design-system';

	import MainStep from './Steps/MainStep.svelte';
	import RoomStep from './Steps/RoomStep.svelte';
	import TypeSelectionStep from './Steps/TypeSelectionStep.svelte';
	import DevicesSelectionStep from './Steps/DevicesSelectionStep.svelte';

	import ExitConfirmationModal from 'src/components/ExitConfirmationModal.svelte';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { getItemRoomID, onItemSaved, orderItemsInRoom } from 'src/helpers/add-entity-helpers';
	import { createAsyncAction } from 'src/helpers/async_action_guard';

	const dispatch = createEventDispatcher();
	const { loading: isSaving, run: runSave } = createAsyncAction();
	const dispatchGroupSaved = createEventDispatcher<{ 'group-saved': {} }>();

	export let editGroup;
	export let editMode = false;
	export let showAddEditGroupModal;

	const previousButton = $format('button_back');
	let wizardItems = setWizardSteps([
		$format('select_group_type', { default: 'Group type' }),
		$format('select_group_devices', { default: 'Devices' }),
		$format('select_group_room', { default: 'Group room' }),
		$format('details_step', { default: 'Details' }),
	]);

	let activeWizardStep = wizardItems[0];
	//#region scroll to variables
	let modalRef: Modal = null;
	let selectedRoomRef: HTMLElement = null;
	let selectedDeviceRef: HTMLElement = null;
	let wizardItemsContainerRef: HTMLElement = null;
	let wizardContainerRef: HTMLElement = null;
	let scrolledToView = false;
	let showExitConfirmationModal = false;
	let exitConfirmationModalState = false;
	let clonedGroupObject = deepCopy(editGroup);
	let initiallyLoadedEditWizard = false;

	const keyPress = (e) => {
		e.stopImmediatePropagation();
	};
	$: if (showAddEditGroupModal) {
		window.addEventListener('keydown', keyPress);
	} else {
		window.removeEventListener('keydown', keyPress);
	}

	$: (activeWizardStep.activeIndex, (scrolledToView = false));
	//#endregion scroll to variables

	let disabled;

	$: isLastStepWizard = activeWizardStep.activeIndex === wizardItems.length - 1;

	$: nextButton = isLastStepWizard ? $format('button_save') : $format('button_next');

	$: isEditGroupWizard = editMode || !!editGroup?.id;
	$: modalTitle = isEditGroupWizard
		? $format('prompt_title_edit_group', {
				default: 'Edit group',
			})
		: $format('prompt_title_add_group', {
				default: 'Add group',
			});

	const onFinish = () =>
		runSave(async () => {
			// if (!validateGroupName(editGroup).isValid) return;
			editGroup.name = editGroup.name.trim();

			const isRoomChanged =
				editGroup?.id &&
				getItemRoomID['groups'](editGroup) !== getItemRoomID['groups']($groups[editGroup?.id]);
			const isNewOrRoomChanged = !editGroup?.id || isRoomChanged;

			// new Group data or room changed
			if (isNewOrRoomChanged) {
				await orderItemsInRoom(editGroup, 'groups');

				editGroup = {
					...editGroup,
					position: 0, // // New/changed room  items should be added at the first position to eliminate the need to scroll when adding a new item
				};
			}

			const res = await requestSaveGroup(editGroup, editGroup?.id);
			if (res?.isok) {
				dispatchGroupSaved('group-saved');
			}

			if (isNewOrRoomChanged) {
				onItemSaved(editGroup, 'groups');
			}

			onCloseAddEditModal(true);
		});

	const onCloseAddEditModal = (isFinished?: boolean) => {
		if (!isFinished && JSON.stringify(editGroup) !== JSON.stringify(clonedGroupObject)) {
			showExitConfirmationModal = true;
			exitConfirmationModalState = false;
		} else {
			dispatch('finish');
			dispatch('close');
			showAddEditGroupModal = false;

			wizardItems = resetWizardItems(wizardItems);
			activeWizardStep = wizardItems[0];
		}
	};

	let disabledConditions = [];

	afterUpdate(() => {
		const notSelectedDevice =
			editGroup.devices.filter((dId) => 'undefined' !== typeof $devices[dId]).length == 0;

		disabledConditions = [
			!editGroup.type,
			!editGroup.type || notSelectedDevice,
			!editGroup.room_id || notSelectedDevice,
			!editGroup.name ||
				editGroup?.name?.trim() == '' ||
				(!editGroup.image && !editGroup.backgroundColor),
		];

		disabled = disabledConditions[activeWizardStep.activeIndex];
	});

	//#region Wizard Functions
	const onClickWizardItem = (index: number) => {
		const result = updateWizardStepOnWizardItem(index, wizardItems, activeWizardStep);
		wizardItems = result.wizardItems;
		activeWizardStep = { ...result.activeWizardStep };

		scrollSelectedCardToView();
	};

	const onClickPrevNextButton = (type: 'prev' | 'next') => {
		const result = updateWizardStepOnPrevNext(type, wizardItems, activeWizardStep);
		wizardItems = result.wizardItems;
		activeWizardStep = { ...result.activeWizardStep };

		scrollSelectedCardToView();
	};
	//#endregion Wizard Functions

	let removeContainerEventListener = () => {};

	//#region Wizard Scroll to Functions
	const scrollSelectedCardToView = () => {
		removeContainerEventListener();

		let elementRef = null;
		let wizardStepIndex = 0;

		if (activeWizardStep.activeIndex === 2 && selectedRoomRef && !scrolledToView) {
			elementRef = selectedRoomRef;
			wizardStepIndex = 2;
		} else if (activeWizardStep.activeIndex === 1 && selectedDeviceRef && !scrolledToView) {
			elementRef = selectedDeviceRef;
			wizardStepIndex = 1;
		}

		if (!elementRef) {
			// Scroll to top, when no card is selected
			modalRef.scrollToTop();
		} else {
			removeContainerEventListener = scrollToElement(
				elementRef,
				wizardItemsContainerRef,
				activeWizardStep.activeIndex,
				wizardStepIndex,
			);
		}

		scrolledToView = true;
	};
</script>

<Modal
	on:close={() => onCloseAddEditModal(false)}
	showModal={showAddEditGroupModal}
	title={modalTitle}
	bind:this={modalRef}
	removeScrollPadding
>
	<Wizard slot="toolbar">
		{#each wizardItems as item, i (i)}
			<WizardItem
				on:click={() => {
					onClickWizardItem(i);
				}}
				active={item.active}
				title={capFirstChar(item.title)}
				disabled={disabledConditions[i - 1]}
				showMoreDots={item.showMoreDots}
				indicatorColor={$showNewLayout ? 'color-2' : undefined}
			/>
		{/each}
	</Wizard>

	<div slot="content">
		<WizardContainer
			activeStepIndex={activeWizardStep.activeIndex}
			bind:wizardItemsContainerRef
			bind:wizardContainerRef
			adaptiveHeight
		>
			{#each wizardItems as _, i (i)}
				<!-- DO NOT REMOVE COMMENT: Padding references -->
				<WizardContent active={activeWizardStep.activeIndex === i} class="pb-s pt-s">
					{#if i === 0}
						<TypeSelectionStep bind:editGroup />
					{:else if i === 1}
						<DevicesSelectionStep
							bind:editGroup
							bind:selectedFirstDevice={selectedDeviceRef}
						/>
					{:else if i === 2}
						<RoomStep bind:editGroup bind:selectedRef={selectedRoomRef} />
					{:else if i === 3}
						<MainStep bind:editGroup />
					{/if}
				</WizardContent>
			{/each}
		</WizardContainer>
	</div>

	<div slot="footer" class="d-flex justify-content-end">
		{#if activeWizardStep.activeIndex > 0}
			<Button
				variant="secondary"
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
				icon="fas fa-chevron-left"
				responsiveSize={{
					sm: 'm',
				}}
				on:click={() => onClickPrevNextButton('prev')}
			>
				{previousButton}
			</Button>
		{/if}

		<Button
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
			icon={isLastStepWizard ? 'fas fa-save' : ''}
			rightIcon={isLastStepWizard ? '' : 'fas fa-chevron-right'}
			responsiveSize={{
				sm: 'm',
			}}
			{disabled}
			loading={isLastStepWizard && $isSaving}
			on:click={() => {
				if (isLastStepWizard) {
					onFinish();
				} else {
					onClickPrevNextButton('next');
				}
			}}
		>
			{nextButton}
		</Button>
	</div>
</Modal>

<ExitConfirmationModal
	type={$format('the_group', { default: 'the group' })}
	{exitConfirmationModalState}
	{showExitConfirmationModal}
	showAddEditModal={showAddEditGroupModal}
	on:close={(e) => {
		exitConfirmationModalState = e.detail.exitConfirmationModalState;
		showExitConfirmationModal = e.detail.showExitConfirmationModal;
	}}
	on:confirm={(e) => {
		dispatch('close');
		showExitConfirmationModal = e.detail.showExitConfirmationModal;
		showAddEditGroupModal = e.detail.showAddEditModal;
	}}
/>
